import { Box, Button, IconButton, Modal, TextField, Typography } from "@mui/material"
import { useMemo, useState } from "react";
import modalStyle from "../../styles/modalStyle";
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import apiClient from "../../axiosClient";
import DropDown from "../baseComponents/dropdown";
import { decodedJWT, getUserRole } from "../../utils/commonUtils";
import { numberPattern, passwordPattern, textPattern } from "../../constants/patterns";
import RoleManagement from "../../security/roleManagement";
import { userRoles } from "../../constants/userRoles";
import { AppDispatch, RootState } from "../../store/store";
import { useSelector } from "react-redux";
import User, { EmploymentType } from "../../interfaces/user";
import { loadUsers } from "../../store/slices/userSlice";
import { useAppDispatch } from "../../store/hooks";
import Client from "../../interfaces/client";
import Agency from "../../interfaces/agency";
import DropDownValue from "../baseComponents/dropdownvalue";
import { protectedTextFieldProps } from "../../utils/protectedTextFieldProps";
import UserService from "../../services/userService";


interface ModalProps {
    openModal: boolean,
    handleCloseModal: () => void,
    reloadFunction: () => void
}

interface UserRequest {
    name: string,
    password: string,
    email: string,
    phone_number: string,
    user_role: string,
    address: string,
    parent_username: string,
    owner_type: string,
    owner_name: string,
    employment_type: EmploymentType | null
}

const AddUserModal: React.FC<ModalProps> = ({ openModal, handleCloseModal, reloadFunction }) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [user, setUser] = useState<UserRequest>({
        name: "",
        email: "",
        phone_number: "",
        address: "",
        user_role: "",
        password: "",
        parent_username: "",
        owner_type: "",
        owner_name: "",
        employment_type: null
    });

    let clients: Client[] = useSelector((state: RootState) => state.clients.clients);
    const agencies: Agency[] = useSelector((state: RootState) => state.agencies.agencies);

    // compute owner‐name options based on owner_type
    const ownerNameOptions = useMemo<string[]>(() => {
        if (user.owner_type === 'Client') {
            return clients
            .map(c => c.client_name)
            .sort((a, b) => a.localeCompare(b));   // sort in asc order
        }
        if (user.owner_type === 'Agency') {
            return agencies
            .map(a => a.agency_name)
            .sort((a, b) => a.localeCompare(b));   // sort in asc order
        }
        return []; // no owner type selected
    }, [user.owner_type]);

    const users = useSelector((state: RootState) => state.userList.users);
    const ownerTypes = [
        { type: "Client" },
        { type: "Agency" }
    ];

    const token = decodedJWT();
    const userRole = getUserRole(token);
    const dispatch: AppDispatch = useAppDispatch();

    const userRoleOptions = RoleManagement.isSuperAdmin(userRole)
        ? userRoles.adminUserRoles
        : token && token.owner_type === 'Client'
        ? userRoles.clientUserRoles
        : token && token.owner_type === 'Agency'
        ? RoleManagement.isAdmin(userRole)
        ? userRoles.agencyUserRoles
        : userRoles.supervisorAllowedUserRoles
        : [];

    const handleSetUser = <K extends keyof UserRequest> (key: K, value:UserRequest[K]) => {
        setUser((prevUser) => ({ ...prevUser, [key]: value }));
    }

    // Handle form submission
    const handleSubmit = async () => {
        try {
            const payload: UserRequest = { ...user };

            if (token && token.owner_type !== 'Client') {
                payload.parent_username = "-1";
            }
            if (!payload.name || !payload.address || !payload.phone_number || !payload.password || !payload.user_role || !payload.parent_username || (RoleManagement.isSuperAdmin(userRole) && !payload.owner_name)) {
                alert('Please fill all required values');
                return;
            } else if (!passwordPattern.test(payload.password)) {
                alert('Please satisfy the following conditions for password:\nAtleast 1 capital letter\nAtleast 1 digit\nAtleast 1 special character\nAtleast 8 characters\nNo spaces');
                return;
            } else if (!payload.employment_type) {
                alert('Please select an Employment Type');
                return;
            }
            // Make POST request to add new user (use your real API endpoint)
            const response = await apiClient.post('/api/v1/user', { ...payload });
            if(response.status === 200){
                alert("Successfully Added User");
                await fetchUserList();
                reloadFunction();
                closeModal(); // Close the modal on success
            }
        } catch (error: any) {
            if (error.response) {
                alert(`Error: ${error.response.data.message}\n${error.response.data.details.map((val: string) => `${val}\n`)}`);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        }
    };

    const fetchUserList = async () => {
        const query = RoleManagement.isSuperAdmin(userRole) ? '?user_role=Admin' : '';
        const response = await UserService.getUsers(query);
        if (response) {
            dispatch(loadUsers(response.user_responses as User[]));
        }
    };

    const closeModal = () => {
        setUser({
            name: "",
            email: "",
            phone_number: "",
            address: "",
            user_role: "",
            password: "",
            parent_username: "",
            owner_type: "",
            owner_name: "",
            employment_type: null
        });
        handleCloseModal();
    };

    return (
        <Modal open={openModal} onClose={closeModal} >
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography id="modal-title" variant="h6">
                        Add New {RoleManagement.isSuperAdmin(userRole) ? 'Admin' : 'User'}
                    </Typography>
                    <IconButton onClick={closeModal}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box mt={2} display="flex" justifyContent="space-between">
                    <Box width="30%">
                        <TextField
                            label="Name"
                            value={user.name}
                            onChange={(e) => handleSetUser('name', e.target.value.replace(textPattern, ""))}
                            fullWidth
                            margin="normal"
                            slotProps={{
                                htmlInput: {
                                    maxLength: 50,
                                },
                            }}
                        />
                    </Box>
                    <Box width="30%">
                        <DropDown label="User Role" items={userRoleOptions} selectedValue={user.user_role} onSelection={(e) => handleSetUser('user_role', e)} />
                    </Box>
                    <Box width="30%">
                        <TextField
                            label="Password"
                            value={user.password}
                            type={showPassword ? 'text' : 'password'}
                            onChange={(e) => handleSetUser('password', e.target.value)}
                            fullWidth
                            margin="normal"
                            slotProps={{
                                htmlInput: {
                                    ...protectedTextFieldProps,
                                    maxLength: 30,
                                },
                                input: {
                                    endAdornment: (
                                        <IconButton onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    )
                                }
                            }}
                            
                        />
                    </Box>
                </Box>
                <Box mt={2} display="flex" justifyContent="space-between">
                    <Box width="48%">
                        <DropDown
                            label="Employment Type"
                            selectedValue={user.employment_type}
                            onSelection={(val) => handleSetUser('employment_type', val)}
                            items={Object.values(EmploymentType)}
                        />
                    </Box>
                </Box>
                <Box mt={2} display="flex" justifyContent="space-between">
                    <Box width="48%">
                        <TextField
                            label="Email"
                            value={user.email}
                            type="email"
                            onChange={(e) => handleSetUser('email', e.target.value.replace(textPattern, ""))}
                            fullWidth
                            margin="normal"
                            slotProps={{
                                htmlInput: {
                                    maxLength: 100,
                                },
                            }}
                        />
                    </Box>
                    <Box width="48%">
                        <TextField
                            label="Phone Number"
                            value={user.phone_number}
                            type="tel"
                            onChange={(e) => handleSetUser('phone_number', e.target.value.replace(numberPattern, "").slice(0, 10))}
                            fullWidth
                            margin="normal"
                        />
                    </Box>
                </Box>
                <Box mt={2} display="flex" justifyContent="space-between">
                    <Box width="100%">
                        <TextField
                            label="Address"
                            value={user.address}
                            type="text"
                            onChange={(e) => handleSetUser('address', e.target.value.replace(textPattern, ""))}
                            fullWidth
                            margin="normal"
                            slotProps={{
                                htmlInput: {
                                    maxLength: 1000,
                                },
                            }}
                        />
                    </Box>
                </Box>
                
                {token && token.owner_type === 'Client' && (
                    <Box mt={2} display="flex" justifyContent="space-between">
                        <Box width="100%">
                            <DropDown
                                label="Select Parent"
                                items={users}               // an array of User
                                keyProp="username"                   // MenuItem key={item.username}
                                valueProp="username"                 // MenuItem value={item.username}
                                displayProp="username"              // renders item.username
                                selectedValue={user.parent_username}     // the currently‐selected username
                                onSelection={(val: string) =>       // val will be the selected username
                                    handleSetUser("parent_username", val)
                                }
                            />
                        </Box>
                    </Box>
                )}

                {RoleManagement.isSuperAdmin(userRole) && (
                    <Box mt={2} display="flex" justifyContent="space-between">
                        <Box width="100%">
                            <DropDown
                                label="Select Owner Type"
                                items={ownerTypes}
                                keyProp="type"
                                valueProp="type"
                                displayProp="type"
                                selectedValue={user.owner_type}
                                onSelection={(val: string) => handleSetUser("owner_type", val)}
                            />
                        </Box>
                    </Box>
                )}

                {RoleManagement.isSuperAdmin(userRole) && (
                    <Box mt={2} display="flex" justifyContent="space-between">
                        <Box width="100%">
                            <DropDownValue
                                label="Owner Name"
                                items={ownerNameOptions}
                                selectedValue={user.owner_name}
                                onSelection={(val: string) => handleSetUser('owner_name', val)}
                                required
                                noOptionsText="Select an owner type first"
                                style={{ mt: 1, mb: 2 }}
                            />
                        </Box>
                    </Box>
                )}
                
                <Box mt={2} textAlign="right">
                    <Button variant="contained" color="primary" onClick={handleSubmit}>
                        Submit
                    </Button>
                </Box>
            </Box>
        </Modal >
    );
}

export default AddUserModal;